import {
	ApiHeaderSearchCategory,
	ApiHeaderSearchResponse,
	ApiHeaderSearchResultItem,
} from '../api-types/header-search.api';
import {
	HEADER_SEARCH_CATEGORIES,
	HeaderSearchCategory,
	HeaderSearchGroupResult,
	HeaderSearchResponse,
} from '../app-types/header-search.app';
import { PERSON_TYPE, SearchResultItem } from '../app-types/search.app';
import { normalizeBoolean, toNullableNumber } from './helpers';
import { mapPhaseNameToPhaseId } from './phase.transform';

const normalizeCategory = (category: ApiHeaderSearchCategory): HeaderSearchCategory | null => {
	const value =
		typeof category === 'number'
			? category
			: typeof category === 'string'
				? HeaderSearchCategory[category as keyof typeof HeaderSearchCategory]
				: null;

	return typeof value === 'number' && HEADER_SEARCH_CATEGORIES.includes(value as HeaderSearchCategory)
		? (value as HeaderSearchCategory)
		: null;
};

const transformResultByCategory = (
	category: HeaderSearchCategory,
	result: ApiHeaderSearchResultItem,
): SearchResultItem | null => {
	const id = toNullableNumber(result.entityId);
	const parentId = toNullableNumber(result.parentEntityId);
	const objectId = result.entityGuid ?? '';

	switch (category) {
		case HeaderSearchCategory.Clients:
			return {
				personType: PERSON_TYPE.CLIENT,
				ableToRepresentAs: toNullableNumber(result.ableToRepresentAs),
				isInYourIntegrationServiceOffice: normalizeBoolean(result.isInYourIntegrationServiceOffice),
				item: {
					id: id ?? 0,
					objectId,
					name: result.name ?? '',
					isArchived: normalizeBoolean(result.isArchived),
					firstName: result.firstName ?? undefined,
					dateOfBirth: result.dateOfBirth ?? undefined,
					user:
						result.consultantLastName || result.consultantFirstName
							? {
									firstName: result.consultantFirstName ?? undefined,
									name: result.consultantLastName ?? undefined,
								}
							: undefined,
				},
			};
		case HeaderSearchCategory.Institutions: {
			const isBranch = parentId != null;
			return {
				personType: isBranch ? PERSON_TYPE.INSTITUTION_BRANCH : PERSON_TYPE.INSTITUTION,
				ableToRepresentAs: toNullableNumber(result.ableToRepresentAs),
				isInYourIntegrationServiceOffice: normalizeBoolean(result.isInYourIntegrationServiceOffice),
				item: {
					id: id ?? 0,
					objectId,
					name: result.name ?? '',
					institutionId: parentId ?? undefined,
					isMainInstitution: !isBranch,
					address:
						result.street || result.houseNumber || result.postalCode || result.city
							? {
									street: result.street ?? undefined,
									housenumber: result.houseNumber ?? undefined,
									postalCode: result.postalCode ?? undefined,
									location: result.city ?? undefined,
								}
							: undefined,
				},
			};
		}
		case HeaderSearchCategory.Users:
			return {
				personType: PERSON_TYPE.USER,
				ableToRepresentAs: toNullableNumber(result.ableToRepresentAs),
				isInYourIntegrationServiceOffice: normalizeBoolean(result.isInYourIntegrationServiceOffice),
				item: {
					id: id ?? 0,
					objectId,
					name: result.name ?? '',
					firstName: result.firstName ?? undefined,
					integrationServiceOffice: result.integrationServiceOfficeName
						? {
								id: 0,
								remark: result.integrationServiceOfficeName,
								shortName: result.integrationServiceOfficeName,
							}
						: undefined,
				},
			};
		case HeaderSearchCategory.Sponsors:
			return {
				personType: PERSON_TYPE.SPONSOR,
				ableToRepresentAs: toNullableNumber(result.ableToRepresentAs),
				isInYourIntegrationServiceOffice: normalizeBoolean(result.isInYourIntegrationServiceOffice),
				item: {
					id: id ?? 0,
					objectId,
					name: result.name ?? '',
					address:
						result.street || result.houseNumber || result.postalCode || result.city
							? {
									street: result.street ?? undefined,
									housenumber: result.houseNumber ?? undefined,
									postalCode: result.postalCode ?? undefined,
									location: result.city ?? undefined,
								}
							: undefined,
				},
			};
		case HeaderSearchCategory.IntegrationServiceOffices:
			return {
				personType: PERSON_TYPE.INTEGRATION_SERVICE_OFFICE,
				ableToRepresentAs: toNullableNumber(result.ableToRepresentAs),
				isInYourIntegrationServiceOffice: normalizeBoolean(result.isInYourIntegrationServiceOffice),
				item: {
					id: id ?? 0,
					objectId,
					name: result.name ?? '',
					remark: result.name ?? undefined,
					address:
						result.street || result.houseNumber || result.postalCode || result.city
							? {
									street: result.street ?? undefined,
									housenumber: result.houseNumber ?? undefined,
									postalCode: result.postalCode ?? undefined,
									location: result.city ?? undefined,
								}
							: undefined,
				},
			};
		case HeaderSearchCategory.Assignments:
			return {
				personType: PERSON_TYPE.ASSIGNMENT,
				ableToRepresentAs: toNullableNumber(result.ableToRepresentAs),
				isInYourIntegrationServiceOffice: normalizeBoolean(result.isInYourIntegrationServiceOffice),
				item: {
					id: id ?? 0,
					objectId,
					name: result.name ?? '',
					clientId: parentId ?? undefined,
					guid: result.assignmentNumber ?? undefined,
					phaseId: mapPhaseNameToPhaseId(result.phaseName),
					categoryTypeOfCare: result.typeOfCareInitial
						? {
								id: 0,
								initial: result.typeOfCareInitial,
								remark: result.typeOfCareInitial,
							}
						: undefined,
					user:
						result.consultantLastName || result.consultantFirstName
							? {
									firstName: result.consultantFirstName ?? undefined,
									name: result.consultantLastName ?? undefined,
								}
							: undefined,
				},
			};
		case HeaderSearchCategory.InstitutionPersons:
			return {
				personType: PERSON_TYPE.INSTITUTION_PERSON,
				ableToRepresentAs: toNullableNumber(result.ableToRepresentAs),
				isInYourIntegrationServiceOffice: normalizeBoolean(result.isInYourIntegrationServiceOffice),
				item: {
					id: id ?? 0,
					objectId,
					name: result.name ?? '',
					firstName: result.firstName ?? undefined,
					institutionName: result.institutionName ?? undefined,
					institutionId: parentId ?? undefined,
				},
			};
		default:
			return null;
	}
};

export const transformApiHeaderSearchResponseToAppHeaderSearchResponse = (
	input: ApiHeaderSearchResponse,
): HeaderSearchResponse => {
	const groups: HeaderSearchGroupResult[] = (input?.groups ?? [])
		.map((group) => {
			const category = normalizeCategory(group?.category ?? null);
			if (!category) return null;

			const items = (group?.results ?? [])
				.map((result) => transformResultByCategory(category, result))
				.filter((result): result is SearchResultItem => !!result)
				.filter((result) => result.item.id != null || !!result.item.objectId);

			return {
				category,
				items,
				hasMore: normalizeBoolean(group?.hasMore),
				rawCount: group?.results?.length ?? 0,
			};
		})
		.filter((group): group is HeaderSearchGroupResult => !!group);

	return { groups };
};
